/**
 * Accepts EAN lists pasted or uploaded as:
 * - ean1,ean2
 * - ean1, ean2
 * - one EAN per line (newlines / Windows \r\n)
 * - mixed commas, spaces, and semicolons
 * - Excel column paste (tabs) and formatted numbers (4.005.900.123.456, 4005900123456.0)
 * Column headers like "EAN" / "GTIN" are ignored.
 */
const EAN_HEADER_TOKENS = new Set([
    "ean",
    "gtin",
    "barcode",
    "upc",
    "ean13",
    "eancode",
    "sku",
    "code",
]);

function isEanHeaderToken(value: string): boolean {
    const token = value.toLowerCase().replace(/[\s_/-]+/g, "");
    return EAN_HEADER_TOKENS.has(token);
}

function sanitizeFormattedText(value: string): string {
    const trimmed = value.trim().replace(/\u00a0/g, " ");
    if (!trimmed) {
        return "";
    }

    if (/^\d+(\.\d+)?e[+\-]?\d+$/i.test(trimmed.replace(/\s/g, ""))) {
        return trimmed;
    }

    if (/^[\d,.\s]+$/.test(trimmed)) {
        const withoutCommas = trimmed.replace(/,/g, "");
        if (/^\d+\.0+$/.test(withoutCommas)) {
            return withoutCommas.split(".")[0];
        }
        return withoutCommas;
    }

    return trimmed;
}

function extractEanCodesFromCell(raw: string): string[] {
    const text = String(raw ?? "").trim();
    if (!text || isEanHeaderToken(text)) {
        return [];
    }

    if (/^\d{4,14}$/.test(text)) {
        return [text];
    }

    const compact = text.replace(/\s/g, "");
    if (/^\d+(\.\d+)?e[+\-]?\d+$/i.test(compact)) {
        return [];
    }

    const sanitized = sanitizeFormattedText(text);
    if (/^\d{4,14}$/.test(sanitized)) {
        return [sanitized];
    }

    const groupingStripped = text.replace(/[.,\s\u00a0]/g, "");
    if (/^\d{4,14}$/.test(groupingStripped)) {
        return [groupingStripped];
    }

    const codes: string[] = [];
    for (const part of text.split(/[\s,;]+/)) {
        const token = part.trim();
        if (/^\d{4,14}$/.test(token)) {
            codes.push(token);
            continue;
        }
        const tokenDigits = token.replace(/[.,\s\u00a0]/g, "");
        if (/^\d{4,14}$/.test(tokenDigits)) {
            codes.push(tokenDigits);
        }
    }
    return codes;
}

/** Parse raw pasted/uploaded text into unique EAN codes. */
export function extractEanCodesFromText(raw: string): string[] {
    const normalized = (raw || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const codes: string[] = [];

    for (const line of normalized.split("\n")) {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
            continue;
        }
        for (const cell of trimmedLine.split("\t")) {
            codes.push(...extractEanCodesFromCell(cell.trim()));
        }
    }

    if (codes.length === 0 && /[,;]/.test(normalized)) {
        for (const part of normalized.split(/[,;]+/)) {
            codes.push(...extractEanCodesFromCell(part.trim()));
        }
    }

    return [...new Set(codes.filter(Boolean))];
}

export function parseEanList(raw: string): string {
    return extractEanCodesFromText(raw).join(",");
}

/** If the term is a list of EANs (including newline-separated), normalize to comma-separated. */
export function normalizeEanSearchTerm(term: string): string {
    const trimmed = (term || "").trim();
    if (!trimmed) {
        return "";
    }
    const eanParts = extractEanCodesFromText(trimmed);
    if (eanParts.length === 0) {
        return trimmed;
    }

    const originalParts = trimmed
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split(/[\s,;\t]+/)
        .map((part) => part.trim())
        .filter(Boolean);
    const hasNonEanToken = originalParts.some(
        (part) =>
            extractEanCodesFromCell(part).length === 0 &&
            !isEanHeaderToken(part),
    );
    if (hasNonEanToken) {
        return trimmed;
    }
    return eanParts.join(",");
}
